import { BusinessDate } from '../date/business-date';
import { DEFAULT_RISK_HORIZON_DAYS } from '../calendar/constants';
import type { ActivityId } from '../shared/ids';
import type { ScheduleEntry } from './schedule-entry';

/**
 * Aviso de material.
 *
 * O sistema **não** gerencia suprimentos: não há pedido, fornecedor, cotação nem estoque.
 * O que ele sabe é o que o cronograma diz — *este material precisa estar na obra nesta
 * data* — e o que o relógio diz. Dessas duas coisas sai a única informação que importa
 * para quem opera: **o que já venceu e o que está para vencer**.
 *
 * Quem confirma a compra é o sistema de compras, lá fora. Aqui o aviso serve para a
 * conversa acontecer antes de a frente parar.
 */

export type MaterialAlertLevel = 'OVERDUE' | 'DUE_SOON' | 'SCHEDULED';

export const ALERT_LABEL: Readonly<Record<MaterialAlertLevel, string>> = {
  OVERDUE: 'Data vencida',
  DUE_SOON: 'Chega logo',
  SCHEDULED: 'No prazo',
};

/**
 * Dias entre duas datas de negócio.
 *
 * Conta calendário, não instante: `2026-09-01` menos `2026-08-30` são dois dias, em
 * qualquer fuso e qualquer horário (RN-120).
 */
export function daysBetween(from: BusinessDate, to: BusinessDate): number {
  const parse = (value: string): number => {
    const [year, month, day] = value.split('-').map(Number);
    return Date.UTC(year!, month! - 1, day);
  };
  return Math.round((parse(to.toString()) - parse(from.toString())) / 86_400_000);
}

/**
 * Classifica uma data de necessidade contra o "hoje" da obra.
 *
 * `OVERDUE` é a data que já passou — o material deveria estar na obra e ninguém disse que
 * está. `DUE_SOON` é o que cai dentro do horizonte de alerta da obra (RN-066), que é
 * configurável justamente porque sete dias de antecedência servem para tijolo e não
 * servem para elevador.
 */
export function classifyAlert(
  requiredDate: string,
  today: BusinessDate,
  horizonDays: number = DEFAULT_RISK_HORIZON_DAYS,
): { level: MaterialAlertLevel; daysUntil: number } {
  const parsed = BusinessDate.parse(requiredDate);
  /* c8 ignore next -- a data já foi validada ao gravar o item (RN-021) */
  if (!parsed.ok) return { level: 'SCHEDULED', daysUntil: 0 };

  const daysUntil = daysBetween(today, parsed.value);

  if (daysUntil < 0) return { level: 'OVERDUE', daysUntil };
  if (daysUntil <= horizonDays) return { level: 'DUE_SOON', daysUntil };
  return { level: 'SCHEDULED', daysUntil };
}

export interface MaterialAlert {
  readonly entryId: string;
  readonly activityId: ActivityId;
  readonly requiredDate: string;
  readonly level: MaterialAlertLevel;
  /** Negativo = venceu há N dias; 0 = é hoje. */
  readonly daysUntil: number;
}

export interface AlertSummary {
  readonly overdue: number;
  readonly dueSoon: number;
  readonly total: number;
  readonly alerts: readonly MaterialAlert[];
}

/**
 * Avisos da obra inteira, do mais atrasado para o mais distante.
 *
 * Função pura sobre os itens já carregados: quem lê do banco é o caso de uso, e a árvore
 * da estrutura já traz esses mesmos itens — nenhuma consulta a mais para mostrar o aviso.
 */
export function summarizeAlerts(
  entries: readonly ScheduleEntry[],
  today: BusinessDate,
  horizonDays: number = DEFAULT_RISK_HORIZON_DAYS,
): AlertSummary {
  const alerts = entries
    .map((entry): MaterialAlert => {
      const { level, daysUntil } = classifyAlert(entry.requiredDate, today, horizonDays);
      return {
        entryId: entry.id,
        activityId: entry.activityId,
        requiredDate: entry.requiredDate,
        level,
        daysUntil,
      };
    })
    .filter((alert) => alert.level !== 'SCHEDULED')
    .sort((a, b) => a.daysUntil - b.daysUntil);

  return {
    overdue: alerts.filter((alert) => alert.level === 'OVERDUE').length,
    dueSoon: alerts.filter((alert) => alert.level === 'DUE_SOON').length,
    total: alerts.length,
    alerts,
  };
}

/** `"venceu há 3 dias"`, `"é hoje"`, `"em 5 dias"` — o texto que vai para a tela. */
export function describeDeadline(daysUntil: number): string {
  if (daysUntil < 0) {
    const days = Math.abs(daysUntil);
    return days === 1 ? 'venceu ontem' : `venceu há ${String(days)} dias`;
  }
  if (daysUntil === 0) return 'é hoje';
  return daysUntil === 1 ? 'é amanhã' : `em ${String(daysUntil)} dias`;
}
